import { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { useDesktopSlides } from "@/hooks/useIsDesktop";
import useMobileScrollDots from "@/hooks/useMobileScrollDots";
import useSlickArrows from "@/hooks/useSlickArrows";
import styles from "./CityBreak.module.css";
import { fetchHolidaysBreaks } from "@/services/api/holidays";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { withRegionPath } from "@/utils/session";
import { beginListingSearch } from "@/utils/listingFilterSession";
import { FiArrowUpRight } from "react-icons/fi";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { FaSuitcase, FaSuitcaseRolling } from "react-icons/fa";

const TABS = [
  { label: "Visa Free", isVisaFree: 1 },
  { label: "Visa on Arrival", isVisaFree: 2 },
  { label: "E-Visa", isVisaFree: 3 },
];

const VISA_BADGE_LABELS = {
  VisaFree: "Visa Free",
  VisaOnArrival: "Visa on Arrival",
  EVisa: "E-Visa",
};

function DiagonalArrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 17 17 7M9 7h8v8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CityCard({ item }) {
  const badge = VISA_BADGE_LABELS[item.visa] || item.visa;
  const href = withRegionPath(`/holiday/list?countryId=${item.countryId}`);
  const startListingSearch = () => beginListingSearch("holiday");

  return (
    <article className={styles.card}>
      <a href={href} className={styles.imageLink} aria-label={item.countryName} onClick={startListingSearch}>
      <div className={styles.imageWrap}>
        <img src={item.imageURL} alt={item.countryName} className={styles.cardImage} />
        <span className={styles.badge}>{badge}</span>
      </div>
      </a>

      <div className={styles.body}>
        <div className={styles.topRow}>
          <h3 className={styles.cardTitle}>
            <a href={href} className={styles.titleLink} onClick={startListingSearch}>{item.countryName}</a>
          </h3>
          {item.departures ? (
            <span className={styles.departures}>
              <FaSuitcase size={12} />
              {item.departures} Packages
            </span>
          ) : null}
        </div>

        <div className={styles.divider} />

        <div className={styles.footer}>
          {item.avgPrice ? (
            <div className={styles.priceBlock}>
              <p className={styles.avgLabel}>Starting Price</p>
              <p className={styles.price}>{getCurrencySymbol("main", 15)}{formatIndianPrice(item.startingPrice)}</p>
            </div>
          ) : null}

          <a href={href} className={styles.viewDealsBtn} onClick={startListingSearch}>
            View Deals
          </a>

          <a href={href} className={styles.viewDealsIcon} aria-label={`View deals for ${item.countryName}`}>
            <FiArrowUpRight size={20} />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function CityBreak({ initialHolidaysBreaks = null }) {
  const { isDesktop, mounted, slidesToShow } = useDesktopSlides();
  const sliderRef = useRef(null);
  const [activeVisaType, setActiveVisaType] = useState(1);
  const [breaksByVisa, setBreaksByVisa] = useState(() => initialHolidaysBreaks || {});

  const activeTab = useMemo(
    () => TABS.find((tab) => tab.isVisaFree === activeVisaType) || TABS[0],
    [activeVisaType]
  );

  const activeBreaks = breaksByVisa[activeVisaType];
  const isLoaded = Array.isArray(activeBreaks);
  const filtered = activeBreaks || [];
  const hasItems = filtered.length > 0;

  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    filtered.length,
    slidesToShow,
    activeVisaType
  );
  const { scrollRef, activeDot, mobileDots, onScroll, goToDot } = useMobileScrollDots(
    filtered.length,
    activeVisaType
  );

  useEffect(() => {
    if (isLoaded) return;

    fetchHolidaysBreaks(activeVisaType)
      .then((response) => {
        setBreaksByVisa((prev) => ({
          ...prev,
          [activeVisaType]: Array.isArray(response?.data) ? response.data : [],
        }));
      })
      .catch((error) => {
        console.error("Failed to load holidays breaks:", error);
        setBreaksByVisa((prev) => ({
          ...prev,
          [activeVisaType]: [],
        }));
      });
  }, [activeVisaType, isLoaded]);

  const slickSettings = {
    dots: false,
    infinite: false,
    centerMode: false,
    speed: 400,
    slidesToShow,
    slidesToScroll: 1,
    arrows: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
  };

  const showSlick = mounted && isDesktop;
  const fewSlides = filtered.length <= slidesToShow;

  const handleTabChange = (isVisaFree) => {
    setActiveVisaType(isVisaFree);
    if (typeof sliderRef.current?.slickGoTo === "function") {
      sliderRef.current.slickGoTo(0);
    }
  };

  // if (!filtered.length && !Object.keys(breaksByVisa).length) {
  //   return null;
  // }

  const showEmpty = isLoaded && !hasItems;

  return (
    <section className={`${styles.section} standard-padding`}>
      <div className={styles.header}>
        <div className={styles.headingBlock}>
          <h2 className={"standard-heading"}>Holidays In Your City Breaks</h2>
          <p className="standard-subheading">
            Travel easier across these mesmerizing destinations!
          </p>
        </div>

        <div className={styles.tabsDesktop} role="tablist" aria-label="Visa types">
          {TABS.map((tab) => (
            <button
              key={tab.isVisaFree}
              type="button"
              role="tab"
              aria-selected={activeVisaType === tab.isVisaFree}
              className={`${styles.tab} ${activeVisaType === tab.isVisaFree ? styles.tabActive : ""}`}
              onClick={() => handleTabChange(tab.isVisaFree)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.tabsMobile} role="tablist" aria-label="Visa types">
        {TABS.map((tab) => (
          <button
            key={tab.isVisaFree}
            type="button"
            role="tab"
            aria-selected={activeVisaType === tab.isVisaFree}
            className={`${styles.mobileTab} ${activeVisaType === tab.isVisaFree ? styles.mobileTabActive : ""
              }`}
            onClick={() => handleTabChange(tab.isVisaFree)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {showEmpty ? (
        <p className="nodata">No data found</p>
      ) : !hasItems ? null : showSlick ? (
        <div
          className={`${styles.slickWrap} ${fewSlides ? styles.alignLeft : ""}`}
          key={activeTab.label}
        >
          <button
            type="button"
            className={`headerArrowBtn ${styles.sidePrev}`}
            aria-label="Previous"
            disabled={!canPrev}
            onClick={() => {
              if (typeof sliderRef.current?.slickPrev === "function") {
                sliderRef.current.slickPrev();
              }
            }}
          >
            <MdChevronLeft size={22} />
          </button>

          <button
            type="button"
            className={`headerArrowBtn ${styles.sideNext}`}
            aria-label="Next"
            disabled={!canNext}
            onClick={() => {
              if (typeof sliderRef.current?.slickNext === "function") {
                sliderRef.current.slickNext();
              }
            }}
          >
            <MdChevronRight size={22} />
          </button>

          <Slider ref={sliderRef} {...slickSettings}>
            {filtered.map((item) => (
              <div key={item.countryId} className={styles.slickSlide}>
                <CityCard item={item} />
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <>
          <div
            ref={scrollRef}
            className={styles.slider}
            onScroll={onScroll}
          >
            {filtered.map((item) => (
              <CityCard key={item.countryId} item={item} />
            ))}
          </div>

          {mobileDots > 1 && (
            <div className={styles.dots} aria-label="Slides">
              {Array.from({ length: mobileDots }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`${styles.dot} ${activeDot === index ? styles.dotActive : ""
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => goToDot(index)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
